import { Observable } from 'rxjs/Observable';
import { empty } from 'rxjs/observable/empty';
import { merge as rxMerge } from 'rxjs/observable/merge';
import { of } from 'rxjs/observable/of';
import { catchError } from 'rxjs/operators/catchError';
import { distinctUntilChanged } from 'rxjs/operators/distinctUntilChanged';
import { filter } from 'rxjs/operators/filter';
import { map } from 'rxjs/operators/map';
import { share } from 'rxjs/operators/share';
import { startWith } from 'rxjs/operators/startWith';
import { subscribeOn } from 'rxjs/operators/subscribeOn';
import { switchMap } from 'rxjs/operators/switchMap';
import { tap } from 'rxjs/operators/tap';
import { Subject } from 'rxjs/Subject';
import { Subscription } from 'rxjs/Subscription';

import {
  CYCLE_HANDLING,
  HttpResourceOptions,
  HubContext,
  HubInfoConfig,
  Logger,
  PageSearchResult,
  RenderingContext,
  RenderingContextQueryResult,
  RenderingContextSearchResult,
  SearchResult,
  SearchResults,
  Site,
  SiteContext,
  SitePage,
  SitePagesQueryResult,
  UrlConfig,
} from './../../api';
import { dbgCounter } from './../debug/debug.utils';
import {
  DEFAULT_HTTP_RESOURCE_OPTIONS,
  httpCreateTimer,
  httpGetJsonResource,
  httpGetStringResource,
  HttpService,
} from './../http/http.utils';
import { anyToString, cloneDeep, freezeDeep } from './../js/js.utils';
import { AnyJson, jsonParse } from './../json/json.utils';
import { NOOP_LOGGER } from './../logger/noop.logger';
import { deepDistinctUntilChanged, shareLast } from './../operators/operators';
import { isAbsoluteURL, isNil, isNotNil } from './../predicates/predicates';
import { wchAddTypings } from './../rendering/rendering';
import { EMPTY_JSON_OBSERVABLE, generateItem, idleFrameScheduler, safeUnsubscribe, thisThenThats } from './../rx/rx.utils';
import {
  pageCloneSitePage,
  pageCreatePartialSiteContextForSitePage,
  pageCreateSiteContextFromSearchResult,
  pageEquals,
  siteContextEquals,
  SiteInformation,
} from './../site/site.utils';
import { StaticResources } from './../static/static.resources';
import { ClientStorage } from './../storage/clientstorage';
import { getBaseUrlFromWindow, cloneURL, parseURL, pathForSearch, urlSlashes, urlToString } from './../url/url.utils';
import {
  EMPTY_RENDERING_CONTEXT,
  KEY_CYCLE,
  KEY_RENDERING_CONTEXT_MAP,
  RenderingContextMap,
  wchAddDebug,
  wchAdjustRenderingContextSearchQuery,
  wchAdjustSitePagesSearchQuery,
  wchBoxFetchLevels,
  wchCycleHandling,
  wchForEachRenderingContext,
  wchGetPageSearchURL,
  wchGetParentPageURL,
  wchGetRenderingContextURL,
  wchGetSearchURL,
  wchGetSiteContextURL,
  wchGetSiteURL,
  wchResolveRenderingContext
} from './wch.utils';

const EMPTY_RENDERING_CONTEXT_SEARCH_RESULT: RenderingContextSearchResult[] = [];
const EMPTY_PAGE_SEARCH_RESULT: PageSearchResult[] = [];
const EMPTY_RENDERING_CONTEXTS: RenderingContext[] = [];
const EMPTY_RENDERING_QUERY: RenderingContextQueryResult = { numFound: 0, renderingContexts: EMPTY_RENDERING_CONTEXTS };
const EMPTY_SITE_PAGES_QUERY: SitePagesQueryResult = { numFound: 0, sitePages: [] };
/*
 * If the search string is empty, we produce an empty search result instead of no search result
 */
const EMPTY_RENDERING_CONTEXTS_SEQUENCE: Observable<RenderingContextQueryResult> = of(EMPTY_RENDERING_QUERY);
const EMPTY_SITE_PAGES_SEQUENCE: Observable<SitePagesQueryResult> = of(EMPTY_SITE_PAGES_QUERY);

const NO_RENDERING_CONTEXT: Observable<RenderingContext> = of(EMPTY_RENDERING_CONTEXT).pipe(
  map(cloneDeep)
);
const UNKNOWN_RENDERING_CONTEXT: Observable<RenderingContext> = of(undefined);

const LOGGER = 'WchService';
const _Subject = Subject;

// per default break cycles
const DEFAULT_CYCLE_HANDLING = CYCLE_HANDLING.BREAK;

/**
 * Extracts the document element from a search result
 *
 * @param aSearchResult   the result
 * @return the document
 */
function _pluckDocument<T>(aSearchResult: SearchResult<T>): T {
  return aSearchResult.document;
}

/**
 * Extracts the site ID from the site
 *
 * @param aSite the site
 * @return the document
 */
function _pluckSiteId(aSite: Site): string {
  return aSite.id;
}

/**
 * Constructs the URL path based on the segemnets
 *
 * @param aSegments     the segments
 * @return the path
 */
function _getPathFromUrlSegments(aSegments: string[]): string {
  // convert the segments
  return urlSlashes(aSegments.map(segment => encodeURIComponent(segment)).join('/'));
}

const _assign = Object.assign;

const BFF_PUBLIC_URL = 'delivery/v1/rendering/';
const BFF_SECURE_URL = 'mydelivery/v1/rendering/';

export class InternalWchService {

  // public methods
  dispose: () => void;
  triggerRefresh: () => void;
  getApiUrl: () => URL;
  getDeliveryUrl: () => URL;
  isPreviewMode: () => boolean;
  getDeliverOrigin: () => string;
  getRenderingContextById: (aID: string | null | undefined, aLevels?: number) => Observable<RenderingContext | null | undefined>;
  getRenderingContextByUrlSegments: (aSegments: string[], aLevels?: number) => Observable<RenderingContext | null | undefined>;
  getRenderingContextByPath: (aPath: string, aLevels?: number) => Observable<RenderingContext | null | undefined>;
  getRenderingContextByPage: (aPage: SitePage, aLevels?: number) => Observable<RenderingContext | null | undefined>;
  getRenderingContextForAppConfig: (aLevels?: number) => Observable<RenderingContext>;
  getRenderingContextsByQuery: (aQuery: string, aLevels?: number) => Observable<RenderingContextQueryResult>;
  getSitePagesByQuery: (aQuery: string) => Observable<SitePagesQueryResult>;
  getCorsWhitelist: () => Observable<string[]>;
  getJsonResource: <T>(aUrl: string, aOptions?: HttpResourceOptions) => Observable<T>;
  getTextResource: (aUrl: string, aOptions?: HttpResourceOptions) => Observable<string>;
  resolveRenderingContext: (aRenderingContext?: RenderingContext, aStrategy?: CYCLE_HANDLING)
    => Observable<RenderingContext | null | undefined>;

  // protected methods
  protected createPollingTrigger: (aOptions?: HttpResourceOptions) => Observable<any>;

  constructor(
    aHttp: HttpService,
    aJsonp: HttpService,
    wchConfig: HubInfoConfig,
    aUrlService: UrlConfig,
    clientStorage: ClientStorage,
    usePublicUrl: Observable<boolean>,
    aLogger?: Logger) {

    // logger
    const logger = aLogger || NOOP_LOGGER;

    // access to the instance
    const that = this;

    const _apiUrl = parseURL(aUrlService.apiUrl);
    const _deliveryUrl = parseURL(aUrlService.deliveryUrl);
    const _deliverOrigin = _deliveryUrl.origin;
    const _isPreviewMode = aUrlService.isPreviewMode;
    const _apiRoot = urlToString(_apiUrl);

    // check if we run with credentials
    const _onWithCredentials = usePublicUrl.pipe(
      map(bPublic => _isPreviewMode || !bPublic),
      distinctUntilChanged()
    );

    /** observable that exposes the current base URL for the BFF. The URL does NOT start with
     * a slash but ends with a slash */
    const _bffBaseUrl: Observable<string> = usePublicUrl.pipe(
      map<boolean, string>(bFlag => bFlag ? BFF_PUBLIC_URL : BFF_SECURE_URL),
      distinctUntilChanged<string>(),
      shareLast()
    );

    // cycle handling strategy
    const _cycleHandlingStrategy: CYCLE_HANDLING = wchCycleHandling(wchConfig.cycleHandlingStrategy) || DEFAULT_CYCLE_HANDLING;
    logger.info('_cycleHandlingStrategy', _cycleHandlingStrategy);

    // levels
    const _fetchLevels = wchBoxFetchLevels(wchConfig.fetchLevels);
    logger.info('_fetchLevels', _fetchLevels);

    // access to static resources as a fallback
    const _staticResources = new StaticResources(aUrlService.baseUrl, aHttp);

    // extract the options
    const _httpResourceOptions = _assign({},
      DEFAULT_HTTP_RESOURCE_OPTIONS,
      wchConfig.httpOptions,
      _isPreviewMode ? wchConfig.httpPreviewOptions : undefined);

    logger.info('HttpResourceOptions', _httpResourceOptions);

    // detect the http service to use
    const _http: HttpService = _httpResourceOptions.useJsonP ? aJsonp : aHttp;

    const _siteInfo = new SiteInformation();

    // the hub context so we can inject it
    const _hubContext: HubContext = {
      apiUrl: cloneURL(_apiUrl),
      deliveryUrl: cloneURL(_deliveryUrl),
      isPreviewMode: _isPreviewMode
    };

    // the trigger
    const _trigger = new _Subject<any>();

    // init the trigger
    const _updateObserver = _trigger.pipe(share());
    const _updateTrigger = _trigger;

    let _siteSubscription: Subscription;

    /*
     * Constructs a refresh trigger for polling operations
     *
     * @param aOptions  the configuration
     * @return the trigger that fired an event whenever polling is supposed to occur
     */
    function _createPollingTrigger(aOptions?: HttpResourceOptions): Observable<any> {
      // options to use
      const options = aOptions || _httpResourceOptions;
      return httpCreateTimer(options);
    }

    /*
     * Fetches the resources from the delivery server
     *
     * @param aUrl    the URL
     * @param aOptions  the options
     * @return observable of the data with its priority
     */
    function _getStaticResource(aUrl: string, aOptions: HttpResourceOptions): Observable<AnyJson> {
      // check
      if (aOptions.useStaticResources) {
        // return from the service
        return _staticResources.get(aUrl).pipe(
          // no empty results
          filter(Boolean)
        );
      }
      // nothing
      return EMPTY_JSON_OBSERVABLE;
    }

    /*
     * Fetches the resource via local storage if available
     *
     * @param aUrl    the URL
     * @param aOptions  the options
     * @return observable of the data with its priority
     */
    function _getLocalStorageResource(aUrl: string, aOptions: HttpResourceOptions): Observable<AnyJson> {
      // check
      if (aOptions.useLocalStorage) {
        // return from the service
        return generateItem<AnyJson>(() => clientStorage.get(aUrl)).pipe(
          // execute later
          subscribeOn(idleFrameScheduler),
          // ignore empty results
          filter(Boolean),
          // ignore potential errors
          catchError(err => EMPTY_JSON_OBSERVABLE)
        );
      }
      // nothing
      return EMPTY_JSON_OBSERVABLE;
    }

    /*
     * Fetches the resource via the standard HTTP way
     *
     * @param aUrl    the URL
     * @param aOptions  the options
     * @return observable
     */
    function _getJsonHttpResource(aUrl: string, aOptions?: HttpResourceOptions): Observable<AnyJson> {
      // construct our trigger
      const options: HttpResourceOptions = aOptions || _httpResourceOptions;
      const trigger = rxMerge(_updateObserver, that.createPollingTrigger(options));
      // select the http driver
      const http: HttpService = options.useJsonP ? aJsonp : aHttp;
      // the URL
      const url = isAbsoluteURL(aUrl) ? aUrl : `${_apiRoot}${aUrl}`;
      // access the cached version
      const resp = _onWithCredentials.pipe(
        switchMap<boolean, AnyJson>(bWithCredentials => httpGetJsonResource(url, http, bWithCredentials, trigger, aOptions))
      );
      // optionally write the result to local storage
      if (aOptions.useLocalStorage) {
        // writing the value to our local cache
        return resp.pipe(
          tap(data => clientStorage.put(aUrl, data))
        );
      }
      // just return as is
      return resp;
    }

    /*
     * Fetches the resource via the standard HTTP way if configured
     *
     * @param aUrl    the URL
     * @param aOptions  the options
     * @return observable
     */
    function _getApiResource(aUrl: string, aOptions: HttpResourceOptions): Observable<AnyJson> {
      // check if we are supposed to use the API
      return aOptions.useApi ? _getJsonHttpResource(aUrl, aOptions) : EMPTY_JSON_OBSERVABLE;
    }

    /*
     * Fetches the resource via the standard HTTP way
     *
     * @param aUrl    the URL
     * @param aOptions  the options
     * @return observable
     */
    function _getStringResource(aUrl: string, aOptions?: HttpResourceOptions): Observable<string> {
      // construct our trigger
      const trigger = rxMerge(_updateObserver, that.createPollingTrigger(aOptions));
      // the URL
      const url = isAbsoluteURL(aUrl) ? aUrl : `${_apiRoot}${aUrl}`;
      // access the cached version
      return _onWithCredentials.pipe(
        switchMap<boolean, string>(bWithCredentials => httpGetStringResource(url, aHttp, bWithCredentials, trigger, aOptions))
      );
    }

    /**
     * Returns the search result documents
     *
     * @param aUrl  the URL
     * @param aOptions  the options
     */
    function _getSearchDocuments<T>(aUrl: string, aOptions?: HttpResourceOptions): Observable<T[]> {
      // execute the search
      return _getStringResource(aUrl, aOptions).pipe(
        map<string, SearchResults<SearchResult<T>>>(jsonParse),
        map<SearchResults<SearchResult<T>>, SearchResult<T>[]>(searchResult => searchResult.documents || []),
        map<SearchResult<T>[], T[]>(searchResults => searchResults.map(_pluckDocument))
      );
    }

    /*
     * Fetches the resource via the configured HTTP way
     *
     * @param aUrl    the URL
     * @param aOptions  the options
     *
     * @return observable
     */
    function _getJsonResource<T>(aUrl: string, aOptions?: HttpResourceOptions): Observable<T> {
      // start measuring
      const id = dbgCounter();
      // options to use
      const options: HttpResourceOptions = aOptions ? Object.assign({}, _httpResourceOptions, aOptions) : _httpResourceOptions;
      // assemble the potential sources
      const fromLocalStorage = _getLocalStorageResource(aUrl, options),
        fromStatic = _getStaticResource(aUrl, options),
        fromApi = _getApiResource(aUrl, options);
      // merge the streams
      return thisThenThats(fromStatic, fromLocalStorage, fromApi).pipe(
        // compare semantically to avoid changes
        deepDistinctUntilChanged<T>()
      );
    }

    /*
     * Clones the given context and injects the site context and the result of the handlebars compilation
     *
     * @param aContext    the rendering context to clone
     * @param aMappings   the handlebars mappings
     * @param aSiteInfo   site infor to resolve the site context
     * @param aOptions    request options
     */
    function _cloneRenderingContext(
      aRenderingContext: RenderingContext,
      aSiteContext: SiteContext,
      aOptions?: HttpResourceOptions): RenderingContext {
      // make a copy of the context, so we don't mess with shared references
      const copiedContext = cloneDeep(aRenderingContext);
      // maintain a list of IDs to resolve references
      const allContexts: RenderingContextMap = {};
      // work on the copy and inject the desired data into each sub-context
      wchForEachRenderingContext(copiedContext, (rc, parent, path) => {
        // the id
        const rcId = anyToString(rc.id);
        // update the site context
        (rc as any).context = {
          ...aSiteContext,
          hub: _hubContext
        };
        // add the typings
        wchAddTypings(rc);
        // add debug info
        wchAddDebug(path, rc);
        // only add if parent is not a cycle
        if (isNotNil(parent) && isNil(parent[KEY_CYCLE])) {
          allContexts[rcId] = rc;
        }
        // add the global resolution map
        rc[KEY_RENDERING_CONTEXT_MAP] = allContexts;
      });
      // returns the copied context
      return copiedContext;
    }

    function _createForSite(
      aSiteContext: SiteContext,
      aRenderingContext: RenderingContext,
      aOptions?: HttpResourceOptions): Observable<RenderingContext> {
      // options to use
      const options = aOptions || _httpResourceOptions;
      // perform an update
      return of(_cloneRenderingContext(aRenderingContext, aSiteContext, options));
    }

    function _createForSites(aSiteInformation: SiteInformation,
      aRenderingContexts: RenderingContextQueryResult,
      aOptions?: HttpResourceOptions): Observable<RenderingContextQueryResult> {
      // options to use
      const options = aOptions || _httpResourceOptions;
      // for each context update the site
      const contexts = aRenderingContexts.renderingContexts;
      // perform an update
      return of({
        numFound: aRenderingContexts.numFound,
        renderingContexts: contexts.map(rc => _cloneRenderingContext(rc, aSiteInformation.getSiteContextById(rc.id), options))
      });
    }

    /*
     * Augments the given of rendering context and keeps it up to date
     *
     * @param aContext   the context
     * @param aOptions    the update uptions
     * @return an observable about the updated contexts
     */
    function _createAugmentedRenderingContext(aRenderingContext: RenderingContext,
      aOptions?: HttpResourceOptions): Observable<RenderingContext> {
      // ID
      const contentId = aRenderingContext.id;
      // when the site changes, we need to recompute everything
      return _getSiteInformation().site.pipe(
        switchMap(site => _createForSite(_getSiteInformation().getSiteContextById(contentId), aRenderingContext, aOptions))
      );
    }

    /*
     * Augments the given array of rendering contexts and keeps them up to date
     *
     * @param aContexts   the contexts
     * @param aOptions    the update uptions
     * @return an observable about the updated contexts
     */
    function _augmentRenderingContexts(
      aRenderingContexts: RenderingContextQueryResult,
      aOptions?: HttpResourceOptions): Observable<RenderingContextQueryResult> {
      // when the site changes, we need to recompute everything
      return _getSiteInformation().site.pipe(
        switchMap(site => _createForSites(_getSiteInformation(), aRenderingContexts, aOptions))
      );
    }

    /**
     * Recursively build the breadcrumb trail
     *
     * @param aSiteContext  the current size context
     * @param aOptions  the resource options
     *
     * @return the sequence of site contexts with new breadcrumb trails
     */
    function _agumentBreadcrumbTrail(aSiteContext: SiteContext, aOptions?: HttpResourceOptions): Observable<SiteContext> {
      // log the breadcrumb
      logger.info('_agumentBreadcrumbTrail', aSiteContext.breadcrumb.map(page => page.id));
      // the site
      const siteId = aSiteContext.site.id;
      // check if the top level object in the trail still has a parent
      const breadcrumb = aSiteContext.breadcrumb;
      const firstPage: SitePage = breadcrumb && (breadcrumb.length > 0) ? breadcrumb[0] : undefined;
      // do we have a parent for this beast
      if (firstPage && firstPage.parentId) {
        // fetch the parent
        return _getParentPageUrl(firstPage.parentId, siteId).pipe(
          switchMap<string, SitePage[]>(url => _getSearchDocuments<SitePage>(url, aOptions)),
          map<SitePage[], SitePage>(sitePages => (sitePages && (sitePages.length > 0)) ? sitePages[0] : undefined),
          distinctUntilChanged(pageEquals),
          switchMap<SitePage, SiteContext>(sitePage => {
            // check if we have a result, else the breadcrumb is complete
            if (isNotNil(sitePage)) {
              // create a new context
              const newContext: SiteContext = _assign({}, aSiteContext);
              newContext.breadcrumb = [pageCloneSitePage(sitePage), ...aSiteContext.breadcrumb];
              // freeze the beast to be safe
              const frozenContext: SiteContext = freezeDeep(newContext);
              // recurse
              return _agumentBreadcrumbTrail(frozenContext, aOptions).pipe(
                startWith(frozenContext)
              );
            }
            // nothing
            return empty<SiteContext>();
          })
        );
      }
      // nothing to resolve
      return empty();
    }

    /**
     * Returns a list of self-augmenting site contexts for this config
     *
     * @param aSitePage the page to get the context for
     * @param aSite the full site
     * @param aOptions the resource options
     *
     * @return the stream of contexts
     */
    function _getSiteContextForSite(aSitePage: SitePage, aSite: Site, aOptions?: HttpResourceOptions): Observable<SiteContext> {
      // get some initial context
      const partialContext: SiteContext = pageCreatePartialSiteContextForSitePage(aSitePage, aSite);
      // resolve the real context
      return _getSiteContextUrl(aSitePage.parentId, aSitePage.id, aSite.id).pipe(
        switchMap<string, SitePage[]>(url => _getSearchDocuments<SitePage>(url, aOptions)),
        map<SitePage[], SiteContext>(sitePages => pageCreateSiteContextFromSearchResult(aSitePage, sitePages, aSite)),
        distinctUntilChanged(siteContextEquals),
        switchMap<SiteContext, SiteContext>(siteContext => _agumentBreadcrumbTrail(siteContext, aOptions).pipe(
          startWith(siteContext)
        )),
        startWith(partialContext),
        tap(siteContext => logger.info('_getSiteContextForSite', siteContext))
      );
    }

    /**
     * Builds a sequence of site contexts
     *
     * @param aSitePage   the page
     * @return the stream of contexts
     */
    function _getSiteContext(aSitePage: SitePage, aOptions?: HttpResourceOptions): Observable<SiteContext> {
      // construct the context
      return _getSiteInformation().site.pipe(
        switchMap<Site, SiteContext>(site => _getSiteContextForSite(aSitePage, site, aOptions))
      );
    }

    /**
     * Constructs a rendering context given the site page
     *
     * @param aSitePage   the site page
     * @param aLevels   the levels
     * @return the context
     */
    function _getRenderingContextBySiteContext(aContentId: string, aOnSiteContext: Observable<SiteContext>, aLevels?: number, aOptions?: HttpResourceOptions): Observable<RenderingContext | null | undefined> {
      // method logger
      const LOG_METHOD = '_getRenderingContextBySiteContext';
      // construct the context
      const onSiteContext: Observable<SiteContext> = aOnSiteContext;
      // construct the URL
      const onRenderingContextUrl = _getRenderingContextURL(aContentId, aLevels);
      // some timing
      const tStart = Date.now();
      // make the request
      return onRenderingContextUrl.pipe(
        // log this
        tap(url => logger.info(LOG_METHOD, 'ID', aContentId, 'levels', aLevels, 'URL', url)),
        // fetch the actual resource
        switchMap<string, RenderingContext>(url => _getJsonResource<RenderingContext>(url, aOptions)),
        // augment
        switchMap<RenderingContext, RenderingContext>(rc => onSiteContext.pipe(
          switchMap<SiteContext, RenderingContext>(siteContext => _createForSite(siteContext, rc)))
        ),
        // log this
        tap(() => logger.info(LOG_METHOD, aContentId, Date.now() - tStart, '[ms].'))
      );
    }

    /**
     * Constructs a rendering context given the site page
     *
     * @param aSitePage   the site page
     * @param aLevels   the levels
     * @return the context
     */
    function _getRenderingContextBySitePage(aSitePage: SitePage, aLevels?: number, aOptions?: HttpResourceOptions): Observable<RenderingContext | null | undefined> {
      // method logger
      const LOG_METHOD = '_getRenderingContextBySitePage';
      // the ID
      const contentId = aSitePage.contentId;
      // construct the context
      const onSiteContext: Observable<SiteContext> = _getSiteContext(aSitePage, aOptions);
      // dispatch
      return _getRenderingContextBySiteContext(contentId, onSiteContext, aLevels, aOptions);
    }

    /**
     * Invoked if a route cannot be resolved. We will not conduct a search for a dynamic route
     *
     * @param aPath the escaped path
     * @param aLevels the requested levels
     * @return the observable of the rendering context
     */
    function _getRenderingContextForDynamicPageByPath(aPath: string, aLevels?: number, aOptions?: HttpResourceOptions): Observable<RenderingContext | null | undefined> {
      // resolve the beast
      return _getSiteInformation().site.pipe(
        map<Site, string>(site => site.id),
        distinctUntilChanged(),
        switchMap<string, string>(siteId => _getPageSearchUrl(pathForSearch(aPath), siteId)),
        switchMap<string, SitePage[]>(url => _getSearchDocuments<SitePage>(url, aOptions)),
        switchMap<SitePage[], RenderingContext>(sitePages => (sitePages.length > 0) ? _getRenderingContextBySitePage(sitePages[0], aLevels, aOptions) : _getRenderingContextById(null, aLevels))
      );
    }

    /*
     * Returns the rendering context given the path
     *
     * @param aPath  the path
     * @param aLevels number of levels to resolve when fetching the context
     *
     * @return an observable with the rendering context
     */
    function _getRenderingContextByPath(aPath: string, aLevels?: number): Observable<RenderingContext | null | undefined> {
      // map the slug to the actual component selection
      return _getIdByPath(aPath).pipe(
        // access the rendering context
        switchMap<string, RenderingContext>(id => (id === null) ? _getRenderingContextForDynamicPageByPath(aPath, aLevels) : _getRenderingContextById(id, aLevels))
      );
    }

    /*
     * Returns the rendering context given the path
     *
     * @param aPage  the page
     * @param aLevels number of levels to resolve when fetching the context
     *
     * @return an observable with the rendering context
     */
    function _getRenderingContextByPage(aSitePage: SitePage, aLevels?: number, aOptions?: HttpResourceOptions): Observable<RenderingContext | null | undefined> {
      // log this
      logger.info('_getRenderingContextByPage', aSitePage, aLevels);
      // check if we know the site context for the page
      const pageId = aSitePage.id;
      const contentId = aSitePage.contentId;
      const siteContext = _getSiteInformation().getSiteContextById(pageId);
      // if the context is known, ideal
      if (isNotNil(siteContext) && isNotNil(siteContext.site)) {
        // return with this context
        return _getRenderingContextBySiteContext(contentId, of(siteContext), aLevels, aOptions);
      }
      // this is a dynamic page, build the context from the page
      return _getRenderingContextBySitePage(aSitePage, aLevels, aOptions);
    }

    /*
     * Returns the rendering context given the URL segments.
     *
     * @param aSegments  the segments
     * @param aLevels number of levels to resolve when fetching the context
     *
     * @return an observable with the rendering context
     */
    function _getRenderingContextByUrlSegments(aSegments: string[], aLevels?: number): Observable<RenderingContext | null | undefined> {
      // map the slug to the actual component selection
      return _getIdByUrlSegments(aSegments).pipe(
        // access the rendering context
        switchMap<string, RenderingContext>(id => (id === null) ? _getRenderingContextForDynamicPageByPath(_getPathFromUrlSegments(aSegments), aLevels) : _getRenderingContextById(id, aLevels))
      );
    }

    /*
     * Returns the rendering context given an element ID. If the ID could not be resolved, then the
     * context will be undefined if the information is unknown to exist, yet, or null if the information
     * is known not to exist.
     *
     * @param aID  the ID
     * @param aLevels number of levels to resolve when fetching the context
     *
     * @return an observable with the rendering context
     */
    function _getRenderingContextById(aID: string | null | undefined, aLevels?: number): Observable<RenderingContext | null | undefined> {
      // only process for valid IDs
      if (aID === undefined) {
        // returns the undefined context
        return UNKNOWN_RENDERING_CONTEXT;
      }
      if (aID === null) {
        // returns the null
        return NO_RENDERING_CONTEXT.pipe(
          // augment
          switchMap<RenderingContext, RenderingContext>(rc => _createAugmentedRenderingContext(rc))
        );
      }
      // construct the URL
      const onRenderingContextUrl = _getRenderingContextURL(aID, aLevels);
      // some timing
      const tStart = Date.now();
      // make the request
      return onRenderingContextUrl.pipe(
        // log this
        tap(url => logger.info('getRenderingContextById', 'ID', aID, 'levels', aLevels, 'URL', url)),
        // fetch the actual resource
        switchMap<string, RenderingContext>(url => _getJsonResource<RenderingContext>(url)),
        // augment
        switchMap<RenderingContext, RenderingContext>(rc => _createAugmentedRenderingContext(rc)),
        // log this
        tap(() => logger.info('getRenderingContextById', aID, Date.now() - tStart, '[ms].'))
      );
    }

    /*
     * Converts a sequence of URL segments into an ID. If the segments could not be mapped
     * to an ID, then the observable will contain undefined if this is because the site has not been loade, yet or
     * null if the page could not be found.
     *
     * @param aSegments   the segments
     */
    function _getIdByUrlSegments(aSegments: string[]): Observable<string | null | undefined> {
      // convert the segments
      const path = _getPathFromUrlSegments(aSegments);
      // decode
      return _getSiteInformation()
        // get the node from path
        .getIdByPath(path).pipe(
          // just some logging
          tap(id => logger.info('getIdByUrlSegments', 'Segments:', '[', aSegments, ']', 'Result:', '[', id, ']'))
        );
    }

    /*
   * Converts a sequence of URL segments into an ID. If the segments could not be mapped
   * to an ID, then the observable will contain undefined if this is because the site has not been loaded, yet or
   * null if the page could not be found.
   *
   * @param aSegments   the segments
   */
    function _getIdByPath(aPath: string): Observable<string | null | undefined> {
      // decode
      return _getSiteInformation()
        // get the node from path
        .getIdByPath(aPath).pipe(
          // just some logging
          tap(id => logger.info('getIdByPath', aPath, id))
        );
    }

    /*
     * Executes the query and returns the rendering contexts.
     *
     * @param aQuery the query string
     * @param aLevels the levels to fetch
     *
     * @return  an observable with the rendering results
     */
    function _getRenderingContextsByQuery(aQuery: string, aLevels?: number): Observable<RenderingContextQueryResult> {
      // only process for valid queries
      if (isNotNil(aQuery)) {
        // construct the URL
        const onQueryUrl = _getQueryURL(wchAdjustRenderingContextSearchQuery(aQuery));
        // make the request
        return onQueryUrl.pipe(
          switchMap<string, SearchResults<RenderingContextSearchResult>>(url => _getJsonResource<SearchResults<RenderingContextSearchResult>>(url)),
          // returns a list of documents
          map<SearchResults<RenderingContextSearchResult>, RenderingContextQueryResult>(searchResults => ({
            numFound: searchResults.numFound,
            renderingContexts: (searchResults.documents || EMPTY_RENDERING_CONTEXT_SEARCH_RESULT)
              .map(_pluckDocument)
          })),
          // compare semantically to avoid changes
          deepDistinctUntilChanged(),
          // log this
          tap(rcs => logger.info('getRenderingContextsByQuery', aQuery, rcs)),
          // augment these contexts
          switchMap<RenderingContextQueryResult, RenderingContextQueryResult>(rcs => _augmentRenderingContexts(rcs))
        );
      } else {
        // nothing
        return EMPTY_RENDERING_CONTEXTS_SEQUENCE;
      }
    }

    /*
    * Executes the query and returns the rendering contexts.
    *
    * @param aQuery the query string
    *
    * @return  an observable with the pages
    */
    function _getSitePagesByQuery(aQuery: string): Observable<SitePagesQueryResult> {
      // only process for valid queries
      if (isNotNil(aQuery)) {
        // construct the URL
        return _getSiteInformation()
          .site.pipe(
            map<Site, string>(_pluckSiteId),
            distinctUntilChanged(),
            switchMap<string, string>(siteId => wchGetSearchURL(wchAdjustSitePagesSearchQuery(aQuery, siteId))),
            distinctUntilChanged(),
            switchMap<string, string>(url => _getStringResource(url)),
            map<string, SearchResults<SearchResult<SitePage>>>(jsonParse),
            map<SearchResults<SearchResult<SitePage>>, SitePagesQueryResult>(searchResult => ({
              numFound: searchResult.numFound,
              sitePages: (searchResult.documents || []).map(_pluckDocument).map(pageCloneSitePage)
            })),
            // make sure we disallow changes
            map(freezeDeep),
            // compare semantically to avoid changes
            deepDistinctUntilChanged()
          );
      } else {
        // nothing
        return EMPTY_SITE_PAGES_SEQUENCE;
      }
    }

    function _getSiteInformation(): SiteInformation {
      // lazily subscribe
      if (isNil(_siteSubscription)) {
        // subscribe
        _siteSubscription = _getSite().subscribe(_siteInfo.observer);
      }
      // returns the value
      return _siteInfo;
    }

    function _getSite(): Observable<Site> {
      // the site URL
      return _getSiteURL().pipe(
        switchMap<string, Site>(url => _getJsonResource<Site>(url))
      );
    }

    function _getRenderingContextURL(aID: string, aLevels?: number): Observable<string> {
      // the base URL
      const levels = wchBoxFetchLevels(aLevels, _fetchLevels);
      // check if we need to add fetch levels
      return _bffBaseUrl.pipe(
        map(baseUrl => wchGetRenderingContextURL(baseUrl, aID)),
        map(url => (levels < 0) ? url : `${url}?levels=${levels}`)
      );
    }

    function _getPageSearchUrl(aPath: string, aSiteId: string): Observable<string> {
      // the base URL
      return wchGetPageSearchURL(aPath, aSiteId);
    }

    function _getSiteContextUrl(aParentId: string | null | undefined, aPageId: string, aSiteId: string): Observable<string> {
      // the base URL
      return wchGetSiteContextURL(aParentId, aPageId, aSiteId);
    }

    function _getParentPageUrl(aParentId: string, aSiteId: string): Observable<string> {
      // the base URL
      return wchGetParentPageURL(aParentId, aSiteId);
    }

    function _getQueryURL(aQuery: string): Observable<string> {
      return _bffBaseUrl.pipe(
        map(baseUrl => `${baseUrl}search?${aQuery}`)
      );
    }

    function _getCorsWhitelistURL(): Observable<string> {
      return _bffBaseUrl.pipe(
        map(baseUrl => `${baseUrl}cors`)
      );
    }

    function _getSiteURL(aSiteID?: string): Observable<string> {
      return _bffBaseUrl.pipe(
        map(baseUrl => wchGetSiteURL(baseUrl, aSiteID))
      );
    }

    function _getCorsWhitelist(): Observable<string[]> {
      return _getCorsWhitelistURL().pipe(
        switchMap<string, string[]>(url => _getJsonHttpResource(url, _httpResourceOptions))
      );
    }

    /* Resolves the rendering context according to the configured strategy */
    function _resolveRenderingContext(aRenderingContext?: RenderingContext,
      aStrategy?: CYCLE_HANDLING): Observable<RenderingContext | null | undefined> {
      // sanity check
      return wchResolveRenderingContext(aRenderingContext, aStrategy || _cycleHandlingStrategy, _getRenderingContextById);
    }

    function _getApiUrl(): URL {
      return _apiUrl;
    }

    function _getDeliveryUrl(): URL {
      return _deliveryUrl;
    }

    function _getPreviewMode(): boolean {
      return _isPreviewMode;
    }

    that.resolveRenderingContext = _resolveRenderingContext;
    that.getRenderingContextById = _getRenderingContextById;
    that.getRenderingContextByUrlSegments = _getRenderingContextByUrlSegments;
    that.getRenderingContextByPath = _getRenderingContextByPath;
    that.getRenderingContextsByQuery = _getRenderingContextsByQuery;
    that.getRenderingContextByPage = _getRenderingContextByPage;
    that.getSitePagesByQuery = _getSitePagesByQuery;
    that.createPollingTrigger = _createPollingTrigger;
    that.getCorsWhitelist = _getCorsWhitelist;
    that.triggerRefresh = (): void => _updateTrigger.next(new Date());
    that.dispose = (): void => safeUnsubscribe(_siteSubscription);
    that.getDeliverOrigin = (): string => _deliverOrigin;
    that.isPreviewMode = _getPreviewMode;
    that.getApiUrl = _getApiUrl;
    that.getDeliveryUrl = _getDeliveryUrl;
    that.getJsonResource = _getJsonResource;
    that.getTextResource = _getStringResource;
  }

}
